<script lang="ts">
  import { BRAND_NAME } from '../lib/brand';
  import { onMount } from 'svelte';
  import { replace } from 'svelte-spa-router';
  import { getInitData, isInsideTelegram } from '../lib/telegram';
  import { consumeDeepLinkRoute, markLandingSettled } from '../lib/nav';
  import { applySession } from '../lib/session';
  import { api, ApiError } from '../lib/api';

  let errorMessage = $state<string | null>(null);
  // Dev-mode hint shown when there's no session AND no remembered telegram_id
  // — points user at the DevStatusSwitcher in the top-right corner.
  let devNeedsLogin = $state(false);

  /** Routes the user after a successful session. A deep link (material/section)
   *  wins over the default landing (for everyone, admins included) so shared
   *  links open straight on their target; otherwise admins land on the role
   *  chooser and everyone else goes to /home. */
  function routeAfterSession(isAdmin: boolean): void {
    const deepLinkRoute = consumeDeepLinkRoute();
    const target = deepLinkRoute ?? (isAdmin ? '/admin-entry' : '/home');
    replace(target);
    // Record the landing route so the back button knows this screen has no
    // in-app predecessor (e.g. a deep-linked material/section opened cold).
    markLandingSettled(target);
  }

  /** Dev-only first-time hydration: if the session cookie exists, use it;
   *  otherwise try the last telegram_id from localStorage. If neither
   *  works, surface a hint and let DevStatusSwitcher take over. */
  async function devBootstrap(): Promise<void> {
    try {
      const dto = await api.authSession();
      applySession(dto);
      routeAfterSession(dto.isAdmin);
      return;
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        errorMessage = 'Не вдалося зв’язатись із бекендом. Перевір що контейнери підняті.';
        return;
      }
    }

    const { getLastDevTelegramId } = await import('../lib/dev-mock');
    const lastId = getLastDevTelegramId();
    if (!lastId) {
      devNeedsLogin = true;
      return;
    }
    try {
      const dto = await api.authDevLogin(lastId);
      applySession(dto);
      routeAfterSession(dto.isAdmin);
    } catch {
      devNeedsLogin = true;
    }
  }

  onMount(async () => {
    if (import.meta.env.DEV && !isInsideTelegram()) {
      await devBootstrap();
      return;
    }

    // Not inside Telegram WebView → show cover page with CTA to bot.
    if (!isInsideTelegram()) {
      replace('/login');
      return;
    }

    try {
      const dto = await api.authMe(getInitData());
      applySession(dto);
      routeAfterSession(dto.isAdmin);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // initData rejected by backend (tampered or expired) → cover page.
        replace('/login');
        return;
      }
      errorMessage = 'Не вдалося перевірити підписку. Спробуй ще раз через хвилину.';
    }
  });
</script>

<!-- Сцена «схід сонця» з бренд-буку: поле світлішає, диск виходить з-за
     горизонту, лінія росте від центру, і вже потім проявляється напис.
     Уночі та сама сцена йде у зворотний бік — різницю дає від'ємна
     затримка (--sun-phase), а не окрема анімація. -->
<div class="splash play">
  <div class="sp-stage">
    <div class="sp-glow"></div>
    <div class="sp-well"><div class="sp-disc"></div></div>
    <div class="sp-line"></div>
    <div class="sp-word">{BRAND_NAME.toUpperCase()}</div>
  </div>

  {#if errorMessage}
    <div class="sp-status sp-error">{errorMessage}</div>
  {:else if devNeedsLogin}
    <div class="sp-status sp-error">DEV: введи telegram_id у перемикачі справа вгорі →</div>
  {:else}
    <div class="sp-status">Готуємо твій простір…</div>
    <div class="sp-bar" aria-hidden="true"><i></i></div>
  {/if}
</div>

<style>
  /* Значення й тайминги — з mockup/index.html (блок «схід сонця»). */
  .splash {
    flex: 1;
    position: relative;
    overflow: hidden;
    background: #f4ebd9;
  }
  :global(:root[data-theme='dark']) .splash {
    background: var(--dark);
  }
  .splash.play {
    animation: fieldCycle var(--sun-cycle) linear infinite;
  }
  :global(:root[data-theme='dark']) .splash.play {
    animation: fieldCycleNight var(--sun-cycle) linear infinite var(--sun-phase);
  }
  @keyframes fieldCycle {
    0% {
      background: #f4ebd9;
    }
    22%,
    50% {
      background: #fbf6ec;
    }
    72%,
    100% {
      background: #f4ebd9;
    }
  }
  @keyframes fieldCycleNight {
    0% {
      background: #1b1815;
    }
    22%,
    50% {
      background: #241f1b;
    }
    72%,
    100% {
      background: #1b1815;
    }
  }

  .sp-stage {
    position: absolute;
    left: 50%;
    top: 44%;
    transform: translate(-50%, -50%);
    width: 216px;
    height: 150px;
  }

  /* Світло поля: повільно й майже непомітно — це атмосфера, не ефект. */
  .sp-glow {
    position: absolute;
    left: 50%;
    bottom: 44px;
    width: 230px;
    height: 115px;
    border-radius: 230px 230px 0 0;
    background: var(--pu2);
    transform: translateX(-50%);
    filter: blur(26px);
    opacity: 0;
  }
  .splash.play .sp-glow {
    animation: glowCycle var(--sun-cycle) linear infinite;
  }
  :global(:root[data-theme='dark']) .splash.play .sp-glow {
    animation-delay: var(--sun-phase);
  }
  @keyframes glowCycle {
    0% {
      opacity: 0;
    }
    22%,
    50% {
      opacity: 0.1;
    }
    72%,
    100% {
      opacity: 0;
    }
  }

  /* Диск підрізаний горизонтом: коло в «колодязі» заввишки в радіус —
     понад половину сонця не видно в жодному кадрі (правило бренд-буку). */
  .sp-well {
    position: absolute;
    left: 50%;
    bottom: 45px;
    width: 92px;
    height: 46px;
    overflow: hidden;
    transform: translateX(-50%);
  }
  .sp-disc {
    position: absolute;
    top: 0;
    left: 0;
    width: 92px;
    height: 92px;
    border-radius: 50%;
    background: var(--pu2);
    transform: translateY(100%);
  }
  .splash.play .sp-disc {
    animation: sunCycle var(--sun-cycle) linear infinite 0.45s both;
  }
  :global(:root[data-theme='dark']) .splash.play .sp-disc {
    animation-delay: var(--sun-phase);
  }
  @keyframes sunCycle {
    0% {
      transform: translateY(100%);
      animation-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
    }
    22% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(0);
      animation-timing-function: cubic-bezier(0.33, 0, 0.5, 1);
    }
    72%,
    100% {
      transform: translateY(100%);
    }
  }

  /* Горизонт росте від центру; його довжина дорівнює ширині напису. */
  .sp-line {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 43px;
    height: 3px;
    border-radius: 2px;
    background: var(--text);
    transform: scaleX(0);
  }
  .splash.play .sp-line {
    animation: drawLine 0.7s cubic-bezier(0.65, 0, 0.35, 1) 0.1s forwards;
  }
  @keyframes drawLine {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }

  .sp-word {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 8px;
    text-align: center;
    font-family: 'Marcellus', Georgia, serif;
    font-size: 15.5px;
    letter-spacing: 0.285em;
    text-indent: 0.285em;
    color: var(--text);
    opacity: 0;
  }
  .splash.play .sp-word {
    animation: wordUp 0.7s ease 1.05s forwards;
  }
  @keyframes wordUp {
    from {
      opacity: 0;
      transform: translateY(9px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  .sp-status {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 74px;
    text-align: center;
    font-size: 13px;
    padding: 0 24px;
    line-height: 1.5;
    color: var(--text-muted);
    opacity: 0;
    animation: fadeSoft 0.7s ease 1.7s forwards;
  }
  /* Помилку показуємо одразу — чекати 1.7 с, поки проявиться пояснення,
     чому вхід не вдався, було б знущанням. */
  .sp-error {
    animation-delay: 0s;
    color: var(--status-inactive);
  }
  @keyframes fadeSoft {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Смуга — той самий горизонт-прогрес, що й у застосунку. */
  .sp-bar {
    position: absolute;
    left: 50%;
    bottom: 56px;
    width: 216px;
    height: 2px;
    transform: translateX(-50%);
    overflow: hidden;
    border-radius: 2px;
    background: var(--border2);
    opacity: 0;
    animation: fadeSoft 0.5s ease 1.8s forwards;
  }
  .sp-bar i {
    position: absolute;
    inset: 0;
    background: var(--pu2);
    transform: scaleX(0);
    transform-origin: left;
    animation: barFill 2.2s cubic-bezier(0.4, 0, 0.2, 1) 1.8s forwards;
  }
  @keyframes barFill {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }

  /* Кадр без руху — фінальна композиція одразу. Восьмисекундний
     нескінченний цикл у WebView коштує батареї, і не всім він потрібен. */
  @media (prefers-reduced-motion: reduce) {
    .splash,
    .splash * {
      animation: none !important;
    }
    .sp-disc {
      transform: translateY(0) !important;
    }
    .sp-line {
      transform: scaleX(1) !important;
    }
    .sp-word,
    .sp-status,
    .sp-bar {
      opacity: 1 !important;
    }
    .sp-glow {
      opacity: 0.08 !important;
    }
    .sp-bar i {
      transform: scaleX(0.35) !important;
    }
  }
</style>
